
import { Bell, Plus, Search, AlertTriangle, Info, Loader2, Edit, Trash2 } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Layout } from "@/components/Layout"
import PublicarAvisoModal from "@/components/PublicarAvisoModal"
import { useAnnouncements, useDeleteAnnouncement } from "@/hooks/useAnnouncements"
import { useAuth } from "@/hooks/useAuth"

const getTipoIcon = (isImportant: boolean) => {
  return isImportant ? (
    <AlertTriangle className="h-4 w-4 text-red-500" />
  ) : (
    <Info className="h-4 w-4 text-blue-500" />
  )
}

const getTipoVariant = (isImportant: boolean) => {
  return isImportant ? "destructive" as const : "secondary" as const
}

const getTipoLabel = (isImportant: boolean) => {
  return isImportant ? "Importante" : "Informativo"
}

const Avisos = () => {
  const { data: avisos, isLoading, error } = useAnnouncements()
  const { profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [editingAnnouncement, setEditingAnnouncement] = useState<any>(null)
  
  const deleteAnnouncement = useDeleteAnnouncement()

  const canManageAnnouncements = profile?.role === 'sindico' || profile?.role === 'administracao'

  const filteredAvisos = avisos?.filter(aviso => {
    // Filtrar avisos expirados
    if (aviso.expires_at && new Date(aviso.expires_at) < new Date()) {
      return false
    }
    
    const searchLower = searchTerm.toLowerCase()
    const title = aviso.title || ""
    const content = aviso.content || ""
    const authorName = aviso.author?.full_name || ""
    
    return title.toLowerCase().includes(searchLower) ||
           content.toLowerCase().includes(searchLower) ||
           authorName.toLowerCase().includes(searchLower)
  }) || []

  const handleEdit = (announcement: any) => {
    setEditingAnnouncement(announcement)
  }

  const handleDelete = async (announcementId: string) => {
    if (confirm("Tem certeza que deseja excluir este aviso?")) {
      try {
        await deleteAnnouncement.mutateAsync(announcementId)
      } catch (error) {
        console.error("Erro ao excluir aviso:", error)
      }
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar avisos: {error.message}
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">Avisos</h1>
            <p className="text-gray-600">Comunicados e informações importantes</p>
          </div>
          {canManageAnnouncements && (
            <PublicarAvisoModal>
              <Button className="ita-button-primary">
                <Plus className="h-4 w-4 mr-2" />
                Novo Aviso
              </Button>
            </PublicarAvisoModal>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar avisos..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredAvisos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? "Nenhum aviso encontrado" : "Nenhum aviso cadastrado"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAvisos.map((aviso) => (
              <Card key={aviso.id} className="ita-card">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      {getTipoIcon(aviso.is_important || false)}
                      <CardTitle className="text-lg font-semibold flex-1">{aviso.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={getTipoVariant(aviso.is_important || false)}>
                        {getTipoLabel(aviso.is_important || false)}
                      </Badge>
                      <Badge variant="outline">
                        {aviso.target_audience === 'all' && '🌐 Todos'}
                        {aviso.target_audience === 'residents' && '🏠 Moradores'}
                        {aviso.target_audience === 'staff' && '👷 Trabalhadores'}
                      </Badge>
                      {canManageAnnouncements && (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(aviso)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(aviso.id)}
                            disabled={deleteAnnouncement.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <CardDescription className="flex items-center space-x-4">
                    <span>Por: {aviso.author?.full_name || "Desconhecido"}</span>
                    <span>•</span>
                    <span>{new Date(aviso.created_at || "").toLocaleDateString('pt-BR')}</span>
                    {aviso.expires_at && (
                      <>
                        <span>•</span>
                        <span className="text-orange-600">
                          Expira em: {new Date(aviso.expires_at).toLocaleDateString('pt-BR')}
                        </span>
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{aviso.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {editingAnnouncement && (
          <PublicarAvisoModal
            announcement={editingAnnouncement}
            isEditing={true}
            onClose={() => setEditingAnnouncement(null)}
          />
        )}
      </div>
    </Layout>
  )
}

export default Avisos
